import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleDownloadAlbaran, handleDownloadSignedAlbaran } from './Global/DownloadUtils';
import DisplayPhotos from './Global/DisplayPhotos';
import { getOrderItemsByOrderId, logError } from '../api';
import InfoToggle from './Global/InfoToggle';
import ItemsInfoRow from './Global/ItemsInfoRow';
import OrderProductsSummary from './Global/OrderProductsSummary';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
// import './Global/Style/DeliveryList.css'; // Comentado para usar el nuevo diseño

const DeliveryForm = ({ order }) => {
  const [openOrderItemId, setOpenOrderItemId] = useState(null);
  const [orderItems, setOrderItems] = useState({});
  const [showPhotosModal, setShowPhotosModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  const formatDate = (date) => new Date(date).toLocaleDateString('es-ES');

  const renderClient = () => {
    return order.client && order.client.name && order.client.lastName1
      ? `${order.client.name} ${order.client.lastName1} ${order.client.lastName2 || ''}`
      : 'Cliente no disponible';
  };
  
  const renderZipCode = () => {
    return order.address && order.address.zipCode
      ? `${order.address.zipCode}`
      : 'N/A';
  };

  const renderCityAndZip = () => {
    if (order.address) {
      const city = order.address.city || '';
      const zipCode = order.address.zipCode || '';
      if (city && zipCode) {
        return `${city} (${zipCode})`;
      } else if (city) {
        return city;
      } else if (zipCode) {
        return `CP: ${zipCode}`;
      }
    }
    return 'N/A';
  };

  const renderPhone = () => {
    return order.client && order.client.phone ? order.client.phone : 'No disponible';
  };

  const renderPhone2 = () => {
    return order.client && order.client.phone2 ? order.client.phone2 : 'No disponible';
  };

  const renderAddress = () => {
    return order.address && order.address.address && order.address.city && order.address.zipCode && order.address.state
      ? `${order.address.address}, ${order.address.city}, ${order.address.zipCode}, ${order.address.state}`
      : 'Dirección no disponible';
  };

  const totalItems = (order.items?.length || 0) + (order.specialProducts?.length || 0);

  const handleOrderItemToggle = async (orderId) => {
    if (openOrderItemId === orderId) {
      setOpenOrderItemId(null);
    } else {
      setOpenOrderItemId(orderId);
    }
  };

  const handleDeliveryPage = () => {
    navigate(`/delivery/dispatch/${order.id}`, { state: { order } });
  };

  const handleDownload = (type) => {
    if (type === 'albaran') {
      handleDownloadAlbaran(order.id);
    } else if (type === 'signedAlbaran') {
      handleDownloadSignedAlbaran(order.id);
    }
  };



  const getStatusBadge = () => {
    const statusConfig = {
      'Entregado': { bg: 'success', icon: '✅', text: 'white' },
      'Pendiente': { bg: 'warning', icon: '⏳', text: 'dark' },
      'En ruta': { bg: 'info', icon: '🚚', text: 'white' },
      'Cancelado': { bg: 'danger', icon: '❌', text: 'white' },
    };
    const config = statusConfig[order.status] || { bg: 'secondary', icon: '📦', text: 'white' };
    return (
      <span className={`badge bg-${config.bg} text-${config.text} px-3 py-2`}>
        {config.icon} {order.status}
      </span>
    );
  };

  return (
    <div className="card border-0 shadow-sm mb-3" data-testid={`delivery-card-${order.id}`}>
      {/* Header - Always visible */}
      <div 
        className="card-header bg-light border-0 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
        data-testid={`delivery-card-header-${order.id}`}
      >
        <div className="row align-items-center">
          <div className="col-12 col-md-8">
            <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 mb-2">
              <h6 className="mb-0 text-primary" data-testid={`order-external-id-${order.id}`}>{order.externalId}</h6>
              {getStatusBadge()}
              {order.hasIncident && (
                <span className="badge bg-danger text-white px-2 py-1">
                  ⚠️ Incidencia
                </span>
              )}
            </div>
            
            <div className="row g-2 text-sm">
              <div className="col-12 col-md-4">
                <div className="d-flex align-items-center gap-1">
                  <span>👤</span>
                  <strong>{renderClient()}</strong>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="d-flex align-items-center gap-1">
                  <span>📞</span>
                  <span>{renderPhone()}</span>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="d-flex align-items-center gap-1">
                  <span>📍</span>
                  <span>{renderCityAndZip()}</span>
                </div>
              </div>
            </div>
            
            <div className="mt-2">
              <OrderProductsSummary order={order} />
            </div>
          </div>
          
          <div className="col-12 col-md-4 text-md-end mt-2 mt-md-0">
            <div className="d-flex flex-column align-items-md-end gap-1">
              <div className="text-success fw-bold">{order.totalAmount}€</div>
              {order.pendingAmount > 0 && (
                <div className="text-warning small">Pendiente: {order.pendingAmount}€</div>
              )}
              <div className="text-muted small">
                {order.timeSlot ? `🕐 ${order.timeSlot.name} ${order.timeSlot.val1}-${order.timeSlot.val2}` : '🕐 Sin horario'}
              </div>
              <div className="text-muted small">
                💳 {order.paymentMethod?.name || 'Método no especificado'}
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center mt-2">
          {isExpanded ? <FaChevronUp className="text-muted" /> : <FaChevronDown className="text-muted" />}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="card-body">
          {/* Contact & Address Info */}
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="card bg-light border-0 h-100">
                <div className="card-body py-3">
                  <h6 className="card-title text-muted mb-2">📞 Contacto</h6>
                  <div className="small mb-1">
                    <strong>Tel 1:</strong> {renderPhone()}
                  </div>
                  <div className="small">
                    <strong>Tel 2:</strong> {renderPhone2()}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card bg-light border-0 h-100">
                <div className="card-body py-3">
                  <h6 className="card-title text-muted mb-2">📍 Dirección</h6>
                  <div className="small">{renderAddress()}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Dates and Payment */}
          <div className="row mb-3">
            <div className="col-md-4">
              <div className="d-flex align-items-center gap-2">
                <span>📅</span>
                <div>
                  <div className="small text-muted">Fecha pedido</div>
                  <div className="fw-bold">{formatDate(order.orderDate)}</div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="d-flex align-items-center gap-2">
                <span>🚚</span>
                <div>
                  <div className="small text-muted">Fecha entrega</div>
                  <div className="fw-bold">{order.deliveryDate ? formatDate(order.deliveryDate) : 'No definida'}</div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="d-flex align-items-center gap-2">
                <span>💳</span>
                <div>
                  <div className="small text-muted">Método de pago</div>
                  <div className="fw-bold">{order.paymentMethod?.name || 'No especificado'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Comments */}
          {(order.internalComments || order.ExternalComments || order.deliveryComments) && (
            <div className="card bg-light border-0 mb-3">
              <div className="card-body py-3">
                <h6 className="card-title text-muted mb-2">💬 Comentarios</h6>
                {order.internalComments && (
                  <div className="small mb-2">
                    <strong className="text-muted">Internos:</strong> {order.internalComments}
                  </div>
                )}
                {order.ExternalComments && (
                  <div className="small mb-2">
                    <strong className="text-muted">Externos:</strong> {order.ExternalComments}
                  </div>
                )}
                {order.deliveryComments && (
                  <div className="small">
                    <strong className="text-muted">Entrega:</strong> {order.deliveryComments}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Products */}
          {totalItems > 0 && (
            <div className="card bg-light border-0 mb-3">
              <div className="card-body py-3">
                <OrderProductsSummary order={order} showDetailed={true} />
                <ItemsInfoRow order={order} />
              </div>
            </div>
          )}

          {/* Photos */}
          {order.hasImage && (
            <div className="mb-3">
              <DisplayPhotos orderId={order.id} showPhotosModal={showPhotosModal} setShowPhotosModal={setShowPhotosModal} />
            </div>
          )}

          {/* Action Buttons */}
          <div className="d-flex flex-column flex-md-row gap-2">
            {!order.isFinalStatus && (
              <button
                onClick={handleDeliveryPage}
                className="btn btn-warning d-flex align-items-center justify-content-center gap-2"
                data-testid={`go-to-delivery-btn-${order.id}`}
              >
                🚚 Ir a Entrega
              </button>
            )}
            
            <button
              onClick={() => handleDownload('albaran')}
              className="btn btn-outline-info d-flex align-items-center justify-content-center gap-2"
              data-testid={`download-albaran-btn-${order.id}`}
            >
              📄 Descargar Albarán
            </button>
            
            {order.hasSignedAlbaran && (
              <button
                onClick={() => handleDownload('signedAlbaran')}
                className="btn btn-outline-success d-flex align-items-center justify-content-center gap-2"
                data-testid={`download-signed-albaran-btn-${order.id}`}
              >
                ✅ Descargar Albarán Firmado
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryForm;
